/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { AuthenticationLoginResponseModel } from '../../models/authenticationLoginResponse.model';
import { isBefore, parseISO } from 'date-fns'
import { Observable, of } from 'rxjs';
import { switchMap, catchError, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { LoggedUser } from '../../models/loggedInUser.model';

/**
 * Service responsible for handling authentication-related operations, including login, logout,
 * token management, and user information retrieval.
 *
 * This service interacts with a backend authentication API and manages the authentication state
 * of the user within the application. It provides methods for logging in, refreshing tokens,
 * checking authentication status, and managing user data.
 *
 * @service AuthService
 */
@Injectable({
    providedIn: 'root',
})
export class AuthService {
    /**
     * Indicates whether the user is currently logged in.
     *
     * @public
     * @type {boolean}
     */
    public isLoggedIn: boolean = false;
    /**
     * Suffix URL for the login endpoint.
     *
     * @public
     * @type {string}
     */
    loginUrlSuffix: string = '/Authentication/Login';
    /**
     * Suffix URL for the autologin endpoint.
     *
     * @public
     * @type {string}
     */
    autologinUrlSuffix: string = '/Authentication/AutoLogin'

    /**
     * Suffix URL for the refresh token endpoint.
     *
     * @public
     * @type {string}
     */
    refreshTokenUrlSuffix: string = '/Authentication/RefreshToken';
    /**
     * Suffix URL for the logout endpoint.
     *
     * @public
     * @type {string}
     */
    logoutUrlSuffix: string = '/Authentication/Logout';
    /**
     * Suffix URL for retrieving current user information.
     *
     * @public
     * @type {string}
     */
    getUserInfoUrlSuffix: string = '/Authentication/CurrentUserInfo';
    /**
     * URL for the login endpoint.
     *
     * @public
     * @type {string}
     */
    get loginUrl(): string { return environment.apiUrl + this.loginUrlSuffix }
    /**
     * URL for the refresh token endpoint.
     *
     * @public
     * @type {string}
     */
    get refreshTokenUrl(): string { return environment.apiUrl + this.refreshTokenUrlSuffix }
    /**
     * URL for the logout endpoint. Getter: environment.apiUrl è valorizzato a runtime da main.ts (loadEnviroments), quindi va letto
     * al momento dell'uso, non alla creazione del servizio; e dopo un ricaricamento
     * della pagina con il token ancora valido il login non viene rieseguito e un URL vuoto farebbe un POST alla pagina corrente.
     *
     * @public
     * @type {string}
     */
    get logoutUrl(): string { return environment.apiUrl + this.logoutUrlSuffix }
    /**
     * URL for retrieving current user information.
     *
     * @public
     * @type {string}
     */
    get getUserInfoUrl(): string { return environment.apiUrl + this.getUserInfoUrlSuffix }

    get autologinUrl(): string { return environment.apiUrl + this.autologinUrlSuffix }

    /**
     * Suffix URL for the SelezionaEnte endpoint (confirms the organization chosen after a "bridge" login).
     *
     * @public
     * @type {string}
     */
    selezionaEnteUrlSuffix: string = '/Authentication/SelezionaEnte'

    /**
     * URL for the SelezionaEnte endpoint.
     *
     * @public
     * @type {string}
     */
    get selezionaEnteUrl(): string { return environment.apiUrl + this.selezionaEnteUrlSuffix }

    /**
     * Initializes the AuthService with the provided HttpClient and Router.
     *
     * @constructor
     * @param {HttpClient} HttpClient - The HttpClient instance for making HTTP requests.
     * @param {Router} _router - The Router instance for navigating between routes.
     */
    constructor(
        private _http: HttpClient,
        private _router: Router
    ) {

    }

    /**
     * Sends a login request to the authentication API with the provided username and password.
     *
     * This method posts the user's credentials to the login URL and returns the response from the server.
     *
     * @param {string} userName - The username of the user trying to log in.
     * @param {string} password - The password of the user trying to log in.
     * @returns {Observable<AuthService>} An observable containing the response from the login API.
     */
    public login(userName: string, password: string): Observable<AuthService> {
        /**
         * Update URL's values using environment value
         */

        // console.log("this.loginUrl:" + this.loginUrl);

        return this._http.post<AuthService>(this.loginUrl, {
            NomeUtente: userName,
            Password: password,
        });
    }

    /**
     * Confirms the organization chosen after a "bridge" login (organizationId empty in the login
     * response) and returns a full authentication response with organizationId valorized.
     *
     * @param {string} idAzienda - Local id (EnteSelezionabile.id) of the chosen organization.
     * @returns {Observable<AuthenticationLoginResponseModel>} The full login response.
     */
    public selezionaEnte(idAzienda: string): Observable<AuthenticationLoginResponseModel> {

        return this._http.post<AuthenticationLoginResponseModel>(this.selezionaEnteUrl, {
            IdAzienda: idAzienda,
        });
    }

    /**
     * True se l'utente corrente è SuperAmministratore: dal flag della risposta di login (disponibile
     * anche nel login "ponte") oppure dall'utente locale già caricato.
     */
    public isSuperAdmin(): boolean {
        return this.getAuthResult()?.superAmministratore === true || this.getUserResult()?.superAmministratore === true;
    }

    /**
     * Conferma l'ente scelto/creato, salva token e utente locale e marca l'utente come loggato.
     *
     * @param {string} idAzienda - Id locale dell'ente.
     */
    public selezionaEnteECaricaUtente(idAzienda: string): Observable<LoggedUser> {
        return this.selezionaEnte(idAzienda).pipe(
            tap((result) => this.setAuthResult(result)),
            switchMap(() => this.getLoggedUserInfo()),
            tap((info: LoggedUser) => {
                this.setUserResult(info);
                this.isLoggedIn = true;
            }),
        );
    }

    public loginAutomatico(codiceAutoLogin: string) {

        console.log('[loginAutomatico] avvio autologin', { autologinUrl: this.autologinUrl, getUserInfoUrl: this.getUserInfoUrl });

        this._http.post<AuthenticationLoginResponseModel>(this.autologinUrl, { CodiceAutoLogin: codiceAutoLogin })
            .pipe(
                switchMap((authData: AuthenticationLoginResponseModel): Observable<LoggedUser> => {
                    console.log('[loginAutomatico] autologin OK, salvo authResult', authData);
                    this.setAuthResult(authData);
                    return this.getLoggedUserInfo();
                }),
                catchError((error: any): Observable<null> => {
                    console.error('[loginAutomatico] errore in autologin o getLoggedUserInfo, redirect a /login', error);
                    this.clearAuthResult();
                    this.clearUserResult();
                    this._router.navigate(["/login"]);
                    return of(null);
                })
            )
            .subscribe((loginData: LoggedUser | null) => {
                if (loginData) {
                    console.log('[loginAutomatico] getLoggedUserInfo OK, salvo userResult e navigo a /dashboard', loginData);
                    this.setUserResult(loginData);
                    this._router.navigate(["/dashboard"]);
                }
            });
    }



    /**
     * Sends a request to refresh the authentication token.
     *
     * This method retrieves the current authentication result and sends a refresh token request
     * to the backend. It returns the observable response from the server.
     *
     * @returns {Observable<AuthenticationLoginResponseModel>} An observable containing the new
     * authentication token response.
     */
    public refreshToken(): Observable<AuthenticationLoginResponseModel> {
        const authResult: AuthenticationLoginResponseModel | null =
            this.getAuthResult();
        return this._http.post<AuthenticationLoginResponseModel>(
            this.refreshTokenUrl,
            {
                Token: authResult?.token,
            },
        );
    }

    /**
     * Logs the user out of the application and clears the authentication data.
     *
     * This method checks if the token is valid before making a logout request. Upon success, it
     * clears the authentication and user data from local storage and navigates to the login page.
     * If the token is not valid, it directly clears the data and navigates to the login page.
     *
     * @returns {Observable<null>} An observable that emits null if the logout process is initiated.
     */
    public logout() {
        if (this.isValidDataExpiryToken()) {
            return this._http.post(this.logoutUrl, null).subscribe({
                next: (_data) => {
                    this.clearAuthResult();
                    this.clearUserResult();
                    this._router.navigate(['/login']);
                },
                error: (_err) => {
                    this.clearAuthResult();
                    this.clearUserResult();
                    this._router.navigate(['/login']);
                },
            });
        } else {
            this.clearAuthResult();
            this.clearUserResult();
            this._router.navigate(['/login']);
            return of(null);
        }
    }

    /**
     * Checks if the user is currently authenticated.
     *
     * This method returns a boolean indicating the login state of the user.
     *
     * @returns {boolean} True if the user is logged in; otherwise, false.
     */
    public isAuthenticated() {
        return this.isLoggedIn;
    }

    /**
     * Retrieves the authentication result from local storage.
     *
     * This method attempts to parse the authentication data stored in local storage and returns
     * it as an AuthenticationLoginResponseModel. If no data is found, it returns null.
     *
     * @returns {AuthenticationLoginResponseModel | null} The parsed authentication result or null
     * if not found.
     */
    getAuthResult(): AuthenticationLoginResponseModel | null {
        const jsonResult: string | null =
            localStorage.getItem('authentication');
        if (jsonResult != null && jsonResult != '') {
            return JSON.parse(jsonResult) as AuthenticationLoginResponseModel;
        } else {
            return null;
        }
    }

    /**
     * Checks if the current authentication token has not expired.
     *
     * This method compares the current time with the expiry time of the token.
     *
     * @returns {boolean} True if the token is still valid; otherwise, false.
     */
    private isValidDataExpiryToken(): boolean {
        const expiry = this.getExpiryToken()
        return expiry !== null && isBefore(new Date(), expiry)
    }

    /**
     * Retrieves the expiry time of the authentication token.
     *
     * This method retrieves the authentication result and returns the token's expiry time as a
     * moment object. If the authentication result is null, it returns null.
     *
     * @returns {moment.Moment | null} The expiry time of the token or null if not found.
     */
    private getExpiryToken(): Date | null {
        const authResult: AuthenticationLoginResponseModel | null =
            this.getAuthResult()
        if (authResult != null) {
            return parseISO(authResult.scadenzaToken)
        } else {
            return null
        }
    }

    /**
     * Clears the authentication result from local storage.
     *
     * This method removes the authentication data stored in local storage.
     */
    public clearAuthResult() {
        localStorage.removeItem('authentication');
    }

    /**
     * Clears the user data from local storage.
     *
     * This method removes the user data stored in local storage.
     */
    public clearUserResult() {
        localStorage.removeItem('user');
    }

    /**
     * Retrieves the current user's information from the backend.
     *
     * This method sends a GET request to the user information URL and returns the observable
     * containing the user's data.
     *
     * @returns {Observable<any>} An observable containing the user information response from the API.
     */
    public getLoggedUserInfo(): Observable<any> {
        return this._http.get(this.getUserInfoUrl);
    }

    /**
     * Saves the logged-in user's information to local storage.
     *
     * This method serializes the user data and stores it in local storage.
     *
     * @param {LoggedUser} result - The user data to store in local storage.
     */
    public setUserResult(result: LoggedUser) {
        localStorage.setItem('user', JSON.stringify(result));
    }

    /**
     * Retrieves the logged-in user's information from local storage.
     *
     * This method attempts to parse the user data stored in local storage and returns it as
     * a LoggedUser object. If no data is found, it returns null.
     *
     * @returns {LoggedUser | null} The parsed user data or null if not found.
     */
    public getUserResult(): LoggedUser | null {
        const jsonResult: string | null = localStorage.getItem('user');
        if (jsonResult != null && jsonResult != '') {
            return JSON.parse(jsonResult) as LoggedUser;
        } else {
            return null;
        }
    }

    /**
     * Saves the authentication result to local storage.
     *
     * This method serializes the authentication data and stores it in local storage.
     *
     * @param {AuthService} result - The authentication data to store in local storage.
     */
    public setAuthResult(result: AuthenticationLoginResponseModel) {
        localStorage.setItem('authentication', JSON.stringify(result))
    }

    /**
     * Retrieves the expiry time of the refresh token.
     *
     * This method retrieves the authentication result and returns the refresh token's expiry time
     * as a moment object. If the authentication result is null, it returns null.
     *
     * @returns {moment.Moment | null} The expiry time of the refresh token or null if not found.
     */
    public getDataExpiryRefreshToken(): Date | null {
        const authResult: AuthenticationLoginResponseModel | null =
            this.getAuthResult()
        if (authResult != null) {
            return parseISO(authResult.scadenzaRefreshToken)
        } else {
            return null
        }
    }

    /**
     * Checks if the current refresh token has not expired.
     *
     * This method compares the current time with the expiry time of the refresh token.
     *
     * @returns {boolean} True if the refresh token is still valid; otherwise, false.
     */
    public isValidExpiryRefreshToken(): boolean {
        const expiry = this.getDataExpiryRefreshToken()
        return expiry !== null && isBefore(new Date(), expiry)
    }
}
